import {
  EFinancingCompany,
  EFinancingProgram,
  ELoanPurpose,
  EPaymentFrequency,
  EProvince,
  ETax,
  getEProvinceList,
} from '@if/core/types'
import { getEnvRuntimeVariable } from '@if/core/utils'
import { MerchantResource } from '@src/types/MerchantResource'

export const useDevTools = getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEV_TOOLS)

export const HOME_PAGE = '/'
export const PREFERS_MODE_SETTING = 'themeMode'
export const EARLIER_LUCAS_DATE = '2023-01-01'

export const CLEAR_CACHE_EXCEPTION_KEY = [PREFERS_MODE_SETTING]

/**
 * Namespaces the logout cache-clear leaves alone. `if:d:` is the `'device'` scope of
 * `definePreference` (@if/core/storage): settings that belong to the browser rather than
 * to the person — the unread pop-up opt-out, the collapsed rail — and must therefore
 * survive logout. `'user'`-scoped preferences (`if:u:`) are swept, as they should be.
 */
export const CLEAR_CACHE_EXCEPTION_PREFIX = ['if:d:']

export enum EThemeMode {
  Dark = 'dark',
  Light = 'light',
}

export enum EMask {
  phoneNumberMask = '[1-]000-000-0000',
  sinMask = '000 000 000',
  postalCode = 'a0a 0a0',
}

export enum ECurrentCustomerStep {
  Draft = 'draft',
  Prequal = 'prequal',
  Credit = 'credit',
  IncomeVerification = 'incomeVerification',
  Contract = 'contract',
  Funding = 'funding',
}

export enum EPayoutRecoveryMethod {
  Retention = 'retention',
  Chargeback = 'chargeback',
}

export const FederalTaxIds: ETax[] = [ETax.GST, ETax.PST, ETax.HST]

export enum MerchantId {
  IfinanceMerchantId = 'alis*20500',
  MedicardMerchantId = 'alis*20878',
  PayCustomerByBankTransfer = 'alis*451',
  PayCustomerByCheque = 'alis*286',
}

export enum ETerms {
  sixMonths = 6,
  twelveMonths = 12,
  eighteenMonths = 18,
  twentyFourMonths = 24,
  thirtyMonths = 30,
  thirtySixMonths = 36,
  fortyTwoMonths = 42,
  fortyEightMonths = 48,
  fiftyFourMonths = 54,
  sixtyMonths = 60,
  sixtySixMonths = 66,
  seventyTwoMonths = 72,
  eightyFourMonths = 84,
}

export const ETermsList: ETerms[] = Object.values(ETerms).filter((value) => typeof value === 'number') as ETerms[]

export const HiddenMerchantCategories = [ELoanPurpose.Dentistry, ELoanPurpose.Medical, ELoanPurpose.Aesthetics]

/**
 * Service categories treated as "medical", where borrower-facing copy says
 * "patient" rather than "client". Kept as one list so the call sites (currently
 * the credit-application / worksheet category limits and the Custom Buydown
 * patient/client wording) don't drift — see {@link isMedicalServiceCategory}.
 */
export const MedicalMerchantCategories = [ELoanPurpose.Medical, ELoanPurpose.Dentistry, ELoanPurpose.Aesthetics]

/** True when the merchant's service category is a medical one (patient wording, not client). */
export const isMedicalServiceCategory = (serviceCategory?: ELoanPurpose | null): boolean =>
  !!serviceCategory && MedicalMerchantCategories.includes(serviceCategory)

const IFINANCE_LIBRARY: MerchantResource = {
  name: 'iFinance',
  href: 'https://ifinancecanada.info/ifinance/resource-library/home',
  download: false,
}
const DENTALCARD_LIBRARY: MerchantResource = {
  name: 'Dentalcard',
  href: 'https://ifinancecanada.info/dentalcard/resource-library/home',
  download: false,
}
const MEDICARD_LIBRARY: MerchantResource = {
  name: 'Medicard',
  href: 'https://ifinancecanada.info/medicard/resource-library/home',
  download: false,
}

export const ResourceLibraryByCategory: Record<ELoanPurpose, MerchantResource> = {
  [ELoanPurpose.Veterinary]: IFINANCE_LIBRARY,
  [ELoanPurpose.GoodsAndServices]: IFINANCE_LIBRARY,
  [ELoanPurpose.HomeSolutions]: IFINANCE_LIBRARY,
  [ELoanPurpose.Dentistry]: DENTALCARD_LIBRARY,
  [ELoanPurpose.Medical]: MEDICARD_LIBRARY,
  [ELoanPurpose.Aesthetics]: MEDICARD_LIBRARY,
}
export const SymTechInsuranceProviderId = 'monolith*29'

export interface FinancingProgramConfig {
  readonly maxFirstPaymentDelayInDays: number
  readonly useSoftHit: boolean
  readonly useHardHit: boolean
  readonly beneficiaryIsRequired: boolean
  readonly requestedLoanAmountIsRequired: boolean
  readonly loanPurposeIdIsRequired: boolean
  readonly useHardHitForSpecificProvince: EProvince[] | null
  readonly useAlwaysSubmit: boolean
  readonly displayPrequalify: boolean
  readonly displayIncomeVerification: boolean
  readonly minIncome: number
  readonly minCombinedIncome: number
  readonly minLoanAmount: number
  readonly maxLoanAmount: number
  readonly maxLoanAmountMedicard: number
  readonly frequencyPayments: EPaymentFrequency[]
  readonly supportedProvinces: EProvince[]
  readonly useJobAddress: boolean
  readonly useMaxPayment: boolean
  readonly requiresCreditStepIntervention: boolean
  readonly displayAmountGrantedCreditTile: boolean
  readonly minTerm: ETerms
  readonly maxTerm: ETerms
  readonly longTerm: ETerms
  readonly largeLoanAmount: number
  readonly baseAmountThreshold: number
  readonly baseAmountMaxTerm: ETerms | 0
  readonly termList: ETerms[]
  readonly allowCoappAfterDecline: boolean
  readonly supportsCoApplicant: boolean
  readonly supportsSinglePayment: boolean
  readonly mustProvideWarrantyContract?: boolean
  readonly worksheetIsNeededForCredit: boolean
  readonly supportsInviteMessages: boolean
  readonly shouldDisplayBankStatementDocument: boolean
  readonly supportsContractUpdateOnExpiry: boolean
  readonly supportsDocumentUploadRequest: boolean
}

export interface FinancingCompanyConfigOptions {
  name: string
  supportedPrograms: EFinancingProgram[]
  privacyPolicyLinkEN: string
  privacyPolicyLinkFR: string
  phone: string
  email: string
  faqUrlEN?: string
  faqUrlFR?: string
  favicon: string
}

export const DefaultProgramConfig: FinancingProgramConfig = {
  maxFirstPaymentDelayInDays: 30,
  useSoftHit: false,
  useHardHit: false,
  beneficiaryIsRequired: false,
  requestedLoanAmountIsRequired: false,
  loanPurposeIdIsRequired: false,
  useHardHitForSpecificProvince: null,
  useAlwaysSubmit: false,
  minIncome: 21000,
  minCombinedIncome: 35000,
  minLoanAmount: 500,
  maxLoanAmount: 40000,
  maxLoanAmountMedicard: 50000,
  displayIncomeVerification: true,
  displayPrequalify: true,
  frequencyPayments: [EPaymentFrequency.Monthly],
  supportedProvinces: getEProvinceList(),
  useJobAddress: true,
  useMaxPayment: true,
  requiresCreditStepIntervention: false,
  minTerm: 12,
  maxTerm: 84,
  longTerm: 72,
  largeLoanAmount: 0,
  baseAmountThreshold: 0,
  baseAmountMaxTerm: 0,
  termList: ETermsList,
  allowCoappAfterDecline: true,
  displayAmountGrantedCreditTile: true,
  supportsCoApplicant: true,
  supportsSinglePayment: false,
  worksheetIsNeededForCredit: true,
  supportsInviteMessages: false,
  shouldDisplayBankStatementDocument: true,
  supportsContractUpdateOnExpiry: false,
  supportsDocumentUploadRequest: false,
}

export const FinancingProgramConfigs: Record<EFinancingProgram, FinancingProgramConfig> = {
  [EFinancingProgram.Auto]: {
    ...DefaultProgramConfig,
    useHardHit: true,
    frequencyPayments: [EPaymentFrequency.Monthly],
  },
  [EFinancingProgram.Products]: {
    ...DefaultProgramConfig,
    allowCoappAfterDecline: false,
    minCombinedIncome: 0,
    minIncome: 0,
    useAlwaysSubmit: true,
    useHardHitForSpecificProvince: [EProvince.Quebec],
    displayPrequalify: false,
    displayIncomeVerification: false,
    frequencyPayments: [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly],
    supportedProvinces: DefaultProgramConfig.supportedProvinces.filter(
      (p) => p !== EProvince.Saskatchewan && p !== EProvince.Manitoba,
    ),
    useJobAddress: false,
    useMaxPayment: false,
    requiresCreditStepIntervention: true,
    minLoanAmount: 750,
    maxTerm: 72,
    termList: ETermsList.filter((term) => term >= ETerms.twelveMonths && term <= ETerms.seventyTwoMonths),
  },
  [EFinancingProgram.Personal]: {
    ...DefaultProgramConfig,
    maxFirstPaymentDelayInDays: 45,
    useSoftHit: true,
    useHardHit: true,
    beneficiaryIsRequired: true,
    requestedLoanAmountIsRequired: true,
    loanPurposeIdIsRequired: true,
    maxTerm: 72,
    longTerm: 84,
    largeLoanAmount: 10000,
    baseAmountThreshold: 3000,
    baseAmountMaxTerm: ETerms.sixtyMonths,
    frequencyPayments: [EPaymentFrequency.Monthly],
    supportsInviteMessages: true,
    supportsContractUpdateOnExpiry: true,
    supportsDocumentUploadRequest: true,
  },
  [EFinancingProgram.IfXpress]: {
    ...DefaultProgramConfig,
    useSoftHit: true,
    useHardHit: true,
    requestedLoanAmountIsRequired: true,
    maxTerm: 72,
    longTerm: 84,
    largeLoanAmount: 10000,
    supportsDocumentUploadRequest: true,
  },
  [EFinancingProgram.MechanicalWarranty]: {
    ...DefaultProgramConfig,
    minCombinedIncome: 0,
    useAlwaysSubmit: true,
    useHardHitForSpecificProvince: [EProvince.Quebec],
    displayPrequalify: false,
    displayIncomeVerification: false,
    frequencyPayments: [EPaymentFrequency.Single, EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly],
    supportedProvinces: DefaultProgramConfig.supportedProvinces.filter(
      (p) => p !== EProvince.Saskatchewan && p !== EProvince.Manitoba,
    ),
    useJobAddress: false,
    useMaxPayment: false,
    requiresCreditStepIntervention: true,
    minLoanAmount: 750,
    displayAmountGrantedCreditTile: false,
    maxTerm: ETerms.thirtySixMonths,
    termList: [ETerms.twelveMonths, ETerms.twentyFourMonths, ETerms.thirtySixMonths],
    supportsCoApplicant: false,
    mustProvideWarrantyContract: true,
    supportsSinglePayment: true,
    worksheetIsNeededForCredit: false,
    supportsInviteMessages: true,
    shouldDisplayBankStatementDocument: false,
    supportsDocumentUploadRequest: true,
  },
  [EFinancingProgram.B2c]: {
    ...DefaultProgramConfig,
    supportsDocumentUploadRequest: true,
  },
  [EFinancingProgram.CreditRsla]: DefaultProgramConfig,
  [EFinancingProgram.AutoRsla]: DefaultProgramConfig,
  // EasyCare is not offered on this dashboard; the entry only keeps the Record complete.
  [EFinancingProgram.EasyCareProtectionPlan]: DefaultProgramConfig,
}

export const FinancingCompanyConfigs: Record<EFinancingCompany, FinancingCompanyConfigOptions> = {
  [EFinancingCompany.Iceberg]: {
    name: 'Iceberg Finance',
    supportedPrograms: [
      EFinancingProgram.Auto,
      EFinancingProgram.Products,
      EFinancingProgram.MechanicalWarranty,
      EFinancingProgram.IfXpress,
    ],
    privacyPolicyLinkEN: 'https://icebergfinance.ca/en/privacy-policy',
    privacyPolicyLinkFR: 'https://icebergfinance.ca/politique-de-confidentialite',
    phone: '1-855-694-0960',
    email: 'info@icebergfinance.ca',
    favicon: '/images/favicon-iceberg.png',
  },
  [EFinancingCompany.IFinance]: {
    name: 'iFinance Canada',
    supportedPrograms: [EFinancingProgram.Personal, EFinancingProgram.IfXpress],
    privacyPolicyLinkEN: 'https://ifinancecanada.com/privacy-policy',
    privacyPolicyLinkFR: 'https://ifinancecanada.com/fr/politique-de-confidentialite',
    phone: '1-888-689-9876',
    email: 'app@ifinancecanada.com',
    faqUrlEN: 'https://ifinancecanada.com/faq-2-2/#merchants',
    faqUrlFR: 'https://ifinancecanada.com/fr/faq/#marchands',
    favicon: '/images/favicon-ifinance.png',
  },
}

export const Constants = {
  SystemUserId: 'system',
  LargeLoanAmount: 10000,
  SupportedDocumentTypes: '.jpg, .jpeg, .png, .pdf, .heic, .heif',
  SupportedDocumentMimeTypes: ['image/jpeg', 'image/png', 'application/pdf', 'image/heic', 'image/heif'],
  MaxYearsToDisplay: 125,
  AmountRequestedDebounceMs: 500,
}

export const PersonalLoanCalculatorConstants = {
  MIN_AMOUNT: 500,
  MAX_AMOUNT: 40000,
  BUSINESS_DAYS_OFFSET: 5,
  MAX_PAYMENT_AMOUNT: 5000,
} as const

export interface PersonalLoanCalculatorTermOption {
  label: string
  value: number
}

export interface PersonalLoanCalculatorPaymentOption {
  label: string
  value: string
  id: string
}

export enum EPermissions {
  HasDetailAccess = 'HasDetailAccess',
  CanCreateApplications = 'CanCreateApplications',
  IsAdmin = 'IsAdmin',
  CanSendWelcomeMessage = 'CanSendWelcomeMessage',
}

/**
 * The credit-rating tiles: fixed ERS ranges, in display order (best first). Mirrors
 * BASE_INTEREST_RATE_OVER_ERS in the Credit API's PersonalLoanCreditNormsAnalyzer,
 * with its open-ended 830+ row split at 850 — the promotion cut-off, and where most
 * negotiated agreements sit. Below 550 isn't an approvable quote, so it has no tile.
 *
 * Ranges only: the rate is resolved server-side from the selected ERS.
 */
export const CreditRatingBands = [
  { minErs: 850, maxErs: null, nameKey: 'calculator.creditRatingOptions.exceptional' },
  { minErs: 830, maxErs: 850, nameKey: 'calculator.creditRatingOptions.excellent' },
  { minErs: 810, maxErs: 830, nameKey: 'calculator.creditRatingOptions.veryGood' },
  { minErs: 770, maxErs: 810, nameKey: 'calculator.creditRatingOptions.good' },
  { minErs: 720, maxErs: 770, nameKey: 'calculator.creditRatingOptions.aboveAverage' },
  { minErs: 700, maxErs: 720, nameKey: 'calculator.creditRatingOptions.average' },
  { minErs: 665, maxErs: 700, nameKey: 'calculator.creditRatingOptions.fair' },
  { minErs: 635, maxErs: 665, nameKey: 'calculator.creditRatingOptions.building' },
  { minErs: 605, maxErs: 635, nameKey: 'calculator.creditRatingOptions.limited' },
  { minErs: 550, maxErs: 605, nameKey: 'calculator.creditRatingOptions.veryLimited' },
] as const

export type CreditRatingBand = (typeof CreditRatingBands)[number]

export interface CreditRatingOption {
  minErs: number
  maxErs: number | null
  nameKey: CreditRatingBand['nameKey']
}
